import { TableColumn, TableRow } from '@rio-cloud/rio-uikit/Table';
import { useIntl } from 'react-intl';

import { VehicleWarningIcon } from '#components/resources/VehiclePlate.tsx';
import { isServiceOrder } from '#domain/Order.ts';
import { getVehicleWarningState } from '#domain/VehicleWarning.ts';
import type { OrderWithSortableKey } from '#utils/orders.util.ts';

import { StopInfo } from '../shared/StopInfo.tsx';
import { OrderState } from './OrderState.tsx';
import type { OrderTableColumnKey } from './OrderTable.tsx';
import { ServiceTypeName } from './ServiceTypeName.tsx';

type OrderTableRowProps = {
    order: OrderWithSortableKey;
    visibleColumnKeys?: OrderTableColumnKey[];
};

const defaultVisibleColumnKeys: OrderTableColumnKey[] = [
    'type',
    'number',
    'customerName',
    'loadingAddressEndDate',
    'unloadingAddressEndDate',
    'tourName',
    'truckName',
    'statusWeight',
];

export const OrderTableRow = ({ order, visibleColumnKeys = defaultVisibleColumnKeys }: OrderTableRowProps) => {
    const intl = useIntl();
    const orderStatusIsOpen = order.status === 'open';
    const truckWarningState = getVehicleWarningState({
        hasOrderCommunicationLicense: order.truck?.hasOrderCommunicationLicense,
        hasPocketDriverDataSource: order.truck?.hasPocketDriverDataSource,
    });
    const primaryStop = isServiceOrder(order) ? order.executionStop : order.loadingStop;

    return (
        <TableRow
            key={order.id}
            id={order.id}
            selectable={orderStatusIsOpen}
            data-testid={`order-table-row-${order.id}`}
        >
            {visibleColumnKeys.map(columnKey => {
                switch (columnKey) {
                    case 'type':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span
                                    className={`uikit-icon ${isServiceOrder(order) ? 'icon-wrench' : 'icon-truck'}`}
                                    title={intl.formatMessage({
                                        id: isServiceOrder(order)
                                            ? 'intl-msg:ordercom-cloud-frontend.order.type.service'
                                            : 'intl-msg:ordercom-cloud-frontend.order.type.transport',
                                    })}
                                />
                            </TableColumn>
                        );
                    case 'number':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span>
                                    {isServiceOrder(order) ? (
                                        <ServiceTypeName value={order.number} category={order.category} />
                                    ) : (
                                        order.number
                                    )}
                                </span>
                            </TableColumn>
                        );
                    case 'customerName':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span>{order.customerName}</span>
                            </TableColumn>
                        );
                    case 'loadingAddressEndDate':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <StopInfo
                                    name={
                                        isServiceOrder(order)
                                            ? order.company || (
                                                  <ServiceTypeName value={order.number} category={order.category} />
                                              )
                                            : primaryStop.name
                                    }
                                    streetAddress={primaryStop.location?.street}
                                    city={primaryStop.location?.city}
                                    from={primaryStop.startTarget}
                                    to={primaryStop.endTarget}
                                    zipCode={primaryStop.location?.zipCode}
                                    country={primaryStop.location?.country}
                                />
                            </TableColumn>
                        );
                    case 'unloadingAddressEndDate':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                {!isServiceOrder(order) && (
                                    <StopInfo
                                        name={order.unloadingStop?.name}
                                        streetAddress={order.unloadingStop?.location?.street}
                                        city={order.unloadingStop?.location?.city}
                                        from={order.unloadingStop?.startTarget}
                                        to={order.unloadingStop?.endTarget}
                                        zipCode={order.unloadingStop?.location?.zipCode}
                                        country={order.unloadingStop?.location?.country}
                                    />
                                )}
                            </TableColumn>
                        );
                    case 'tourName':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span className='white-space-nowrap' title={order.tourName}>
                                    {order.tourName}
                                </span>
                            </TableColumn>
                        );
                    case 'truckName':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey}>
                                <span className='white-space-nowrap' title={order.truck?.name}>
                                    {order.truck?.name}
                                    <VehicleWarningIcon warningState={truckWarningState} />
                                </span>
                            </TableColumn>
                        );
                    case 'statusWeight':
                        return (
                            <TableColumn key={columnKey} columnKey={columnKey} className='white-space-nowrap'>
                                <span className='white-space-nowrap'>
                                    {order.status && <OrderState status={order.status} />}
                                </span>
                            </TableColumn>
                        );
                    default:
                        return null;
                }
            })}
        </TableRow>
    );
};
